import { FC } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { useDelayedMissed } from "../../hooks/useDelayedMissed";
import { DelayedMissedItem } from "../../domain/activitySidebar.interface";

interface DelayedMissedListProps {
	coordinatorId: string | undefined;
}

const MS_PER_HOUR = 3_600_000;

const formatPlanned = (iso: string | null): string =>
	iso ? dayjs(iso).format("MMM DD, hh:mm A") : "—";

// How long past the expected time, humanized (no dayjs plugin dependency).
export const formatOverdue = (iso: string | null): string => {
	if (!iso) {
		return "";
	}
	const ms = Date.now() - new Date(iso).getTime();
	if (ms <= 0) {
		return "due now";
	}
	const hours = ms / MS_PER_HOUR;
	if (hours < 1) {
		return `${Math.max(1, Math.round(hours * 60))}m overdue`;
	}
	if (hours < 24) {
		return `${Math.floor(hours)}h overdue`;
	}
	return `${Math.floor(hours / 24)}d overdue`;
};

// Wrap a CSV field: quote and escape embedded quotes.
const QUOTE = String.fromCharCode(34); // a double-quote character
const csvCell = (value: string): string =>
	QUOTE + value.split(QUOTE).join(QUOTE + QUOTE) + QUOTE;

export const buildCsv = (items: DelayedMissedItem[]): string => {
	const header = [
		"Participant",
		"Program",
		"Activity",
		"Status",
		"Expected",
		"Overdue",
	];
	const rows = items.map((item) =>
		[
			item.participantName || "",
			item.programName || "",
			item.eventName || "",
			item.missed ? "Missed" : "Delayed",
			item.plannedFor ? dayjs(item.plannedFor).format("YYYY-MM-DD HH:mm") : "",
			formatOverdue(item.plannedFor),
		]
			.map(csvCell)
			.join(","),
	);
	return [header.map(csvCell).join(","), ...rows].join("\r\n");
};

// SIMS-4178: the "Delayed & Missed" list — overdue automatic activities for the
// coordinator, longest-overdue first, with a CSV export of the fetched rows.
export const DelayedMissedList: FC<DelayedMissedListProps> = ({
	coordinatorId,
}) => {
	const navigate = useNavigate();
	const { data, isLoading } = useDelayedMissed(coordinatorId);
	const items = data?.items ?? [];

	const handleExport = (): void => {
		const csv = buildCsv(items);
		// Prepend a UTF-8 BOM so Excel (Windows) detects the encoding and renders
		// accented participant names correctly instead of mojibake.
		const bom = String.fromCharCode(0xfeff);
		const blob = new Blob([bom + csv], { type: "text/csv;charset=utf-8;" });
		const url = URL.createObjectURL(blob);
		const link = document.createElement("a");
		link.href = url;
		link.download = `delayed-missed-${dayjs().format("YYYYMMDD-HHmm")}.csv`;
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		URL.revokeObjectURL(url);
	};

	if (isLoading) {
		return (
			<Typography variant="body2" color="text.secondary">
				Loading…
			</Typography>
		);
	}

	if (items.length === 0) {
		return (
			<Typography variant="body2" color="text.secondary">
				No delayed or missed activities
			</Typography>
		);
	}

	return (
		<Box>
			<Button
				size="small"
				aria-label="Export delayed and missed activities as CSV"
				startIcon={<FileDownloadIcon fontSize="small" aria-hidden={true} />}
				onClick={handleExport}
				sx={{ mb: 0.5, textTransform: "none" }}
			>
				Export CSV
			</Button>

			<List dense disablePadding>
				{items.map((item) => {
					const overdue = formatOverdue(item.plannedFor);
					return (
						<ListItem key={item.udtId} disablePadding>
							<ListItemButton
								aria-label={`View participant ${item.participantName || "Participant"}`}
								onClick={() => navigate(`/participants/${item.beneficiaryId}`)}
								sx={{ alignItems: "flex-start", gap: 1 }}
							>
								<ListItemText
									sx={{ my: 0, minWidth: 0 }}
									primary={item.participantName || "Participant"}
									secondary={
										(item.eventName || "Activity") +
										` · ${formatPlanned(item.plannedFor)}` +
										`${overdue ? ` · ${overdue}` : ""}` +
										`${item.programName ? ` · ${item.programName}` : ""}`
									}
								/>
								{/* Inline status chip (not secondaryAction) so it can't
								    overlap the multi-line text in the narrow rail. */}
								<Chip
									size="small"
									label={item.missed ? "Missed" : "Delayed"}
									color={item.missed ? "error" : "warning"}
									sx={{ flexShrink: 0, mt: 0.25 }}
								/>
							</ListItemButton>
						</ListItem>
					);
				})}
			</List>
		</Box>
	);
};

export default DelayedMissedList;
